import { PencilSquareIcon } from '@heroicons/react/20/solid';
import React from 'react';
import dayjs from 'dayjs';
import NoCoupon from '../../../assets/no-coupon.svg';
import {
  TWDataTable,
  EmptyState,
  StatusBadge,
  TWButton,
} from '../../../components/ui';
import { headerCls } from '../../../data/columns';
import { MENU_FIELD_COPY } from '../constants';
import { menuNamesLabel } from './coupon-menus';

function LegacyCouponList({
  couponList,
  loading,
  pagination,
  onPaginationChange,
  onEdit,
}) {
  const activeCoupons = (couponList || []).filter((x) => x.isactive);

  if (!loading && activeCoupons.length === 0) {
    return <EmptyState image={NoCoupon} title="No Coupons" />;
  }

  const columns = [
    {
      title: <span className={headerCls}>Name</span>,
      dataIndex: 'name',
      key: 'name',
      render: (value) => (
        <span className="font-medium text-slate-900 dark:text-slate-100">{value}</span>
      ),
    },
    {
      title: <span className={headerCls}>Code</span>,
      dataIndex: 'code',
      key: 'code',
      render: (value) => (
        <span className="font-mono text-[13px] text-slate-600 dark:text-slate-300">{value}</span>
      ),
    },
    {
      title: <span className={headerCls}>Min Price</span>,
      dataIndex: 'minprice',
      key: 'minprice',
      render: (value) => (
        <span className="tabular-nums text-slate-600 dark:text-slate-300">${value}</span>
      ),
    },
    {
      title: <span className={headerCls}>Rate</span>,
      key: 'rate',
      render: (_, coupon) => (
        <span className="tabular-nums text-slate-600 dark:text-slate-300">
          {coupon.rate ? `${coupon.rate}%` : `$${coupon.fixrate}`}
        </span>
      ),
    },
    {
      title: <span className={headerCls}>{MENU_FIELD_COPY.column}</span>,
      key: 'menus',
      render: (_, coupon) => {
        const label = menuNamesLabel(coupon.menunames);
        const restricted = label !== MENU_FIELD_COPY.any;
        return (
          <span
            title={restricted ? label : undefined}
            className={`block max-w-[14rem] truncate text-sm ${restricted ? 'text-slate-600 dark:text-slate-300' : 'text-slate-500 dark:text-slate-400'}`}
          >
            {label}
          </span>
        );
      },
    },
    {
      title: <span className={headerCls}>Used</span>,
      dataIndex: 'usagecount',
      key: 'used',
      render: (value) => (
        <span className="tabular-nums text-slate-600 dark:text-slate-300">{value}</span>
      ),
    },
    {
      title: <span className={headerCls}>Total Discount</span>,
      dataIndex: 'totaldiscount',
      key: 'totaldiscount',
      render: (value) => (
        <span className="tabular-nums font-semibold text-slate-700 dark:text-slate-200">
          ${value}
        </span>
      ),
    },
    {
      title: <span className={headerCls}>Active</span>,
      key: 'status',
      render: (_, coupon) => {
        const isActive = dayjs(coupon.expiredate).isAfter(dayjs());
        return (
          <StatusBadge
            tone={isActive ? 'emerald' : 'rose'}
            label={isActive ? 'Active' : 'Expired'}
          />
        );
      },
    },
    {
      title: <span className="sr-only">Actions</span>,
      key: 'actions',
      align: 'right',
      width: 60,
      render: (_, coupon) => (
        <TWButton
          variant="ghost"
          size="sm"
          icon={PencilSquareIcon}
          onClick={(e) => {
            e.stopPropagation();
            onEdit(coupon);
          }}
          aria-label="Edit coupon"
        />
      ),
    },
  ];

  return (
    <TWDataTable
      columns={columns}
      data={activeCoupons}
      loading={loading}
      rowKey="id"
      onRowClick={(coupon) => onEdit(coupon)}
      pagination={{
        current: pagination.pageNumber,
        pageSize: pagination.pageSize,
        total: pagination.totalRecords,
        showTotal: (total, range) =>
          `Showing ${range[0]} to ${range[1]} of ${total} results`,
        onChange: (page) => onPaginationChange(page),
      }}
    />
  );
}

export default LegacyCouponList;
